import type { EAutomationType, TCronSchedule } from "./automation";

// NOTE: NSAI-8009 — Manus automations into NSAI agents (scheduled tasks and
// webhooks). Manus has no automation API, so the server reads them through a
// read-only Manus task (the "export") that the client polls; the enums mirror
// the server's `manus-automation-import.types.ts`.
export enum EManusAutomationExportStatus {
  RUNNING = "running",
  COMPLETED = "completed",
}

export enum EManusAutomationUnsupportedReason {
  EVENT_SOURCE = "eventSource",
  SCHEDULE = "schedule",
  ONE_TIME_PASSED = "oneTimePassed",
  NO_TRIGGER = "noTrigger",
  NO_INSTRUCTION = "noInstruction",
}

export enum EManusAutomationWarning {
  SCRIPT = "script",
  PAYLOAD = "payload",
  CONNECTORS = "connectors",
  EXTRA_TRIGGERS = "extraTriggers",
  WEBHOOK_URL = "webhookUrl",
  WEBHOOK_FILTERS = "webhookFilters",
  EXPIRY = "expiry",
}

export interface IManusAutomationPreview {
  id: string;
  name: string;
  description: string | null;
  instruction: string;
  isPausedInManus: boolean;
  // NOTE: Null when the automation cannot be imported; `unsupportedReason`
  // then says why.
  agentType: EAutomationType | null;
  // NOTE: The exact schedule a scheduled task would be created with.
  schedule: TCronSchedule | null;
  unsupportedReason: EManusAutomationUnsupportedReason | null;
  unsupportedEvent: string | null;
  warnings: EManusAutomationWarning[];
  connectorNames: string[];
}

export interface IManusAutomationExport {
  status: EManusAutomationExportStatus;
  automations?: IManusAutomationPreview[];
}

export interface IManusAutomationExportStart {
  exportId: string;
}

export interface IStartManusAutomationExportPayload {
  apiKey: string;
  // NOTE: Cancels the request in flight; never sent to the server.
  signal?: AbortSignal | undefined;
}

export interface IGetManusAutomationExportPayload {
  apiKey: string;
  exportId: string;
  // NOTE: Cancels the request in flight; never sent to the server.
  signal?: AbortSignal | undefined;
}

export interface IReadManusAutomationsPayload {
  apiKey: string;
  // NOTE: Resumes polling an export started earlier with the same key instead
  // of starting (and paying Manus credits for) a new one.
  exportId?: string | undefined;
  signal?: AbortSignal | undefined;
  onExportStarted?: ((exportId: string) => void) | undefined;
}

export interface IManusAutomationsRead {
  exportId: string;
  automations: IManusAutomationPreview[];
}

export interface IImportManusAutomationRequest {
  automationId: string;
  modelUuid: string;
}

export interface IImportManusAutomationsPayload {
  apiKey: string;
  exportId: string;
  automations: IImportManusAutomationRequest[];
}

export interface IManusAutomationImportResult {
  automationId: string;
  success: boolean;
  agentType?: EAutomationType;
  agentUuid?: string;
  name?: string;
  error?: string;
  // NOTE: Client-side only — set on rows whose batch request failed or was never
  // sent after an earlier batch had already succeeded, so the outcome is unknown.
  isOutcomeUnknown?: boolean;
}

// NOTE: The `manusAutomationImport` keys a selection row can show — literal so
// strict key checking proves each one exists in the namespace.
export type TManusAutomationNoticeKey =
  | "selectAutomations.warnings.script"
  | "selectAutomations.warnings.payload"
  | "selectAutomations.warnings.connectors"
  | "selectAutomations.warnings.extraTriggers"
  | "selectAutomations.warnings.webhookUrl"
  | "selectAutomations.warnings.webhookFilters"
  | "selectAutomations.warnings.expiry"
  | "selectAutomations.unsupported.eventSource"
  | "selectAutomations.unsupported.schedule"
  | "selectAutomations.unsupported.oneTimePassed"
  | "selectAutomations.unsupported.noTrigger"
  | "selectAutomations.unsupported.noInstruction";

export type TManusAutomationAgentTypeKey =
  | "selectAutomations.agentType.scheduledTask"
  | "selectAutomations.agentType.webhook"
  | "selectAutomations.agentType.unsupported";

// NOTE: Every value any notice interpolates, built once per automation and
// passed with each of its keys — one fixed shape, so a key from the union
// always finds the values it names. A type, not an interface: i18next's
// options demand an index signature, which only a type alias gets implicitly.
export type TManusAutomationNoticeParams = {
  placeholder: string;
  names: string;
  event: string;
};

export interface IBuildManusAutomationSelectionsProps {
  automations: IManusAutomationPreview[];
  defaultModelUuid: string;
}
